import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import {
  OverheadExpense,
  OverheadCategory,
  OverheadFrequency,
  OverheadScalesWith,
  OVERHEAD_CATEGORIES,
  OVERHEAD_CATEGORY_LABELS,
  OVERHEAD_FREQUENCIES,
  OVERHEAD_FREQUENCY_LABELS,
  OVERHEAD_SCALES_WITH_LABELS,
  clampOverheadRecurringDay,
  clampOverheadRenewalMonth,
  formatOrdinalDay,
  OVERHEAD_MONTH_OPTIONS,
  resolveOverheadRecurringDay,
  resolveOverheadRenewalMonth,
} from '../../types/finances';
import type { PurchaseOverheadGroup } from '../../types/purchasing';
import { overheadMonthlyContribution } from '../../utils/financesForecast';
import { PURCHASE_OVERHEAD_GROUPS, isPurchaseOverheadGroup } from '../../utils/purchasingSpendClass';

interface OverheadEditModalProps {
  initial: Partial<OverheadExpense> | null;
  /** Current count of active payroll positions, for the per-employee preview. */
  activeHeadcount?: number;
  onClose: () => void;
  onSave: (data: Partial<OverheadExpense>) => Promise<void>;
  onDelete?: () => Promise<void>;
}

function toInputDate(d: Date | null | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function fromInputDate(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);

const OverheadEditModal: React.FC<OverheadEditModalProps> = ({
  initial,
  activeHeadcount = 0,
  onClose,
  onSave,
  onDelete,
}) => {
  const isEdit = !!initial?.id;

  const [name, setName] = useState(initial?.name ?? '');
  const [category, setCategory] = useState<OverheadCategory>(initial?.category ?? 'other');
  const [amount, setAmount] = useState<string>(
    initial?.amount != null ? String(initial.amount) : '',
  );
  const [frequency, setFrequency] = useState<OverheadFrequency>(initial?.frequency ?? 'monthly');
  const [scalesWith, setScalesWith] = useState<OverheadScalesWith>(initial?.scalesWith ?? 'fixed');
  const [startDate, setStartDate] = useState<string>(toInputDate(initial?.startDate ?? new Date()));
  const [recurringDay, setRecurringDay] = useState<number>(
    resolveOverheadRecurringDay({
      startDate: initial?.startDate ?? new Date(),
      recurringDay: initial?.recurringDay,
    }),
  );
  const [renewalMonth, setRenewalMonth] = useState<number>(
    resolveOverheadRenewalMonth({
      startDate: initial?.startDate ?? new Date(),
      renewalMonth: initial?.renewalMonth,
    }),
  );
  const [endDate, setEndDate] = useState<string>(toInputDate(initial?.endDate ?? null));
  const [vendor, setVendor] = useState(initial?.vendor ?? '');
  const [opexGroup, setOpexGroup] = useState<PurchaseOverheadGroup | ''>(() => {
    const group = initial?.opexGroup;
    return isPurchaseOverheadGroup(group) ? group : '';
  });
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [isActive, setIsActive] = useState<boolean>(initial?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const numericAmount = useMemo(() => {
    const v = parseFloat(amount);
    return Number.isFinite(v) ? v : 0;
  }, [amount]);

  const unitMonthly = useMemo(() => {
    const start = fromInputDate(startDate) ?? new Date();
    const draft: OverheadExpense = {
      id: 'preview',
      name,
      category,
      amount: numericAmount,
      frequency,
      scalesWith: 'fixed',
      startDate: start,
      recurringDay,
      renewalMonth: frequency === 'annually' ? renewalMonth : null,
      endDate: fromInputDate(endDate),
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
      createdBy: 'preview',
    };
    return overheadMonthlyContribution(draft, start);
  }, [numericAmount, frequency, name, category, startDate, recurringDay, renewalMonth, endDate]);

  const monthlyEquivalent = useMemo(() => {
    if (scalesWith === 'per_active_employee') return unitMonthly * activeHeadcount;
    return unitMonthly;
  }, [unitMonthly, scalesWith, activeHeadcount]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (numericAmount <= 0) {
      setError('Amount must be greater than zero');
      return;
    }
    const startParsed = fromInputDate(startDate);
    if (!startParsed) {
      setError('Start date is required');
      return;
    }
    const day = clampOverheadRecurringDay(recurringDay);
    if (frequency !== 'one-time' && day == null) {
      setError('Recurring day must be between 1 and 31');
      return;
    }
    if (frequency === 'annually' && clampOverheadRenewalMonth(renewalMonth) == null) {
      setError('Choose the month this annual charge renews');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        category,
        amount: numericAmount,
        frequency,
        scalesWith,
        startDate: startParsed,
        recurringDay: day,
        renewalMonth: frequency === 'annually' ? clampOverheadRenewalMonth(renewalMonth) : null,
        endDate: fromInputDate(endDate),
        vendor: vendor.trim() || undefined,
        notes: notes.trim() || undefined,
        isActive,
        opexGroup: isPurchaseOverheadGroup(opexGroup) ? opexGroup : null,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!window.confirm('Delete this expense? This cannot be undone.')) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete expense');
      setSaving(false);
    }
  };

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div className="fin-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit overhead expense' : 'New overhead expense'}>
        <div className="fin-modal-header">
          <h3>{isEdit ? 'Edit overhead expense' : 'New overhead expense'}</h3>
          <button className="fin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="fin-modal-body">
          {error && <div className="fin-modal-error">{error}</div>}

          <div className="fin-modal-grid">
            <div className="fin-modal-field full">
              <label className="fin-modal-label" htmlFor="overhead-name">Name</label>
              <input
                id="overhead-name"
                className="fin-modal-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g., Office rent — main shop"
                autoFocus
              />
            </div>

            <div className="fin-modal-field">
              <label className="fin-modal-label" htmlFor="overhead-category">Category</label>
              <select
                id="overhead-category"
                className="fin-modal-select"
                value={category}
                onChange={e => setCategory(e.target.value as OverheadCategory)}
              >
                {OVERHEAD_CATEGORIES.map(c => (
                  <option key={c} value={c}>{OVERHEAD_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>

            <div className="fin-modal-field">
              <label className="fin-modal-label" htmlFor="overhead-vendor">Vendor (optional)</label>
              <input
                id="overhead-vendor"
                className="fin-modal-input"
                value={vendor}
                onChange={e => setVendor(e.target.value)}
                placeholder="e.g., Property LLC"
              />
            </div>

            <div className="fin-modal-field full">
              <label className="fin-modal-label" htmlFor="overhead-opex-group">OpEx group</label>
              <select
                id="overhead-opex-group"
                className="fin-modal-select"
                value={opexGroup}
                onChange={e => setOpexGroup(e.target.value as PurchaseOverheadGroup | '')}
              >
                <option value="">Ungrouped</option>
                {PURCHASE_OVERHEAD_GROUPS.map(group => (
                  <option key={group.id} value={group.id}>{group.label}</option>
                ))}
              </select>
              <span className="fin-modal-help">
                Purchasing bucket for this recurring line. You can also drag the row on the OpEx page.
              </span>
            </div>

            <div className="fin-modal-field">
              <label className="fin-modal-label" htmlFor="overhead-amount">Amount (USD)</label>
              <input
                id="overhead-amount"
                className="fin-modal-input"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div className="fin-modal-field">
              <label className="fin-modal-label" htmlFor="overhead-frequency">Frequency</label>
              <select
                id="overhead-frequency"
                className="fin-modal-select"
                value={frequency}
                onChange={e => setFrequency(e.target.value as OverheadFrequency)}
              >
                {OVERHEAD_FREQUENCIES.map(f => (
                  <option key={f} value={f}>{OVERHEAD_FREQUENCY_LABELS[f]}</option>
                ))}
              </select>
            </div>

            <div className="fin-modal-field full">
              <label className="fin-modal-label" htmlFor="overhead-scales">Scales with</label>
              <select
                id="overhead-scales"
                className="fin-modal-select"
                value={scalesWith}
                onChange={e => setScalesWith(e.target.value as OverheadScalesWith)}
              >
                <option value="fixed">{OVERHEAD_SCALES_WITH_LABELS.fixed}</option>
                <option value="per_active_employee">{OVERHEAD_SCALES_WITH_LABELS.per_active_employee}</option>
              </select>
              <span className="fin-modal-help">
                Use "Per active employee" for uniforms, PPE, cell stipends, or any
                cost that scales with headcount.
              </span>
            </div>

            <div className="fin-modal-field">
              <label className="fin-modal-label" htmlFor="overhead-start">Start date</label>
              <input
                id="overhead-start"
                className="fin-modal-input"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
              <span className="fin-modal-help">
                First month this hits monthly burn and the forecast.
              </span>
            </div>

            <div className="fin-modal-field">
              <label className="fin-modal-label" htmlFor="overhead-end">End date (optional)</label>
              <input
                id="overhead-end"
                className="fin-modal-input"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>

            {frequency !== 'one-time' && (
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="overhead-recurring-day">Recurring day</label>
                <select
                  id="overhead-recurring-day"
                  className="fin-modal-select"
                  value={recurringDay}
                  onChange={e => setRecurringDay(Number(e.target.value))}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>{formatOrdinalDay(day)}</option>
                  ))}
                </select>
                <span className="fin-modal-help">
                  Day of the month this bills. Shorter months use the last day.
                </span>
              </div>
            )}

            {frequency === 'annually' && (
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="overhead-renewal-month">Renews in</label>
                <select
                  id="overhead-renewal-month"
                  className="fin-modal-select"
                  value={renewalMonth}
                  onChange={e => setRenewalMonth(Number(e.target.value))}
                >
                  {OVERHEAD_MONTH_OPTIONS.map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
                <span className="fin-modal-help">
                  Calendar month this annual charge renews.
                </span>
              </div>
            )}

            <div className="fin-modal-field full">
              <label className="fin-modal-label" htmlFor="overhead-notes">Notes</label>
              <textarea
                id="overhead-notes"
                className="fin-modal-textarea"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional notes (cancellation terms, renewal date, etc.)"
              />
            </div>

            <div className="fin-modal-field full">
              <label className="fin-modal-toggle">
                <span className="fin-switch">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={e => setIsActive(e.target.checked)}
                  />
                  <span className="fin-switch-slider" />
                </span>
                <span>Active — include in monthly burn and forecast</span>
              </label>
            </div>

            <div className="fin-modal-field full">
              <div className="fin-kpi-card" style={{ padding: '12px 14px' }}>
                <span className="fin-kpi-label">Monthly equivalent</span>
                <span className="fin-kpi-value" style={{ fontSize: 18 }}>
                  {formatCurrency(monthlyEquivalent)}
                </span>
                {scalesWith === 'per_active_employee' && (
                  <span className="fin-kpi-sub">
                    {formatCurrency(unitMonthly)} / mo × {activeHeadcount} active employee
                    {activeHeadcount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="fin-modal-footer">
          {isEdit && onDelete && (
            <button
              type="button"
              className="fin-secondary-btn"
              style={{ color: '#991B1B', borderColor: '#FECACA' }}
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}
          <div className="fin-modal-footer-right">
            <button type="button" className="fin-secondary-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="fin-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add expense'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverheadEditModal;
